/**
 * SystemLayout - Layout para páginas do System Admin (MASTER)
 */

import { memo, type ReactNode } from 'react';
import { LogOut, User } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { usePermissions } from '@/hooks/usePermissions';
import { SystemSidebar } from './SystemSidebar';
import { MasterOrgSelector } from './MasterOrgSelector';
import { Button } from '@/components/ui/button';

interface SystemLayoutProps {
  children: ReactNode;
}

export const SystemLayout = memo(function SystemLayout({
  children,
}: SystemLayoutProps) {
  const { user, logout } = useAuthStore();
  const { isMaster } = usePermissions();

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50 dark:bg-zinc-900">
      {/* Sidebar */}
      <SystemSidebar />

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-zinc-200 bg-white px-6 dark:border-zinc-800 dark:bg-zinc-950">
          <div>
            <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">
              {user?.role === 'org_owner' ? 'Gerenciar Organização' : 'System Administration'}
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {user?.role === 'org_owner' ? 'Gerencie os tenants da sua organização' : 'Multi-tenant management platform'}
            </p>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {/* MASTER-only org selector — ORG_OWNER is locked to their own org */}
            {isMaster && <MasterOrgSelector />}

            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-50">
                  {user?.name || 'Master User'}
                </span>
                <span className="text-xs capitalize text-zinc-500 dark:text-zinc-400">
                  {user?.role || 'master'}
                </span>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
});
